import type { StatementTicketView, StatementView } from './customer-statement-view';

export { printHtmlInHiddenWindow } from '@/modules/dashboard/sales-report-print';

export interface CustomerStatementPrintStrings {
  title: string;
  generated_at_prefix: string;
  phone_label: string;
  email_label: string;
  tickets_label: string;
  liquidados_label: string;
  parciales_label: string;
  pendientes_label: string;
  sin_saldo_label: string;
  total_charges_label: string;
  paid_label: string;
  pending_balance_label: string;
  ticket_col: string;
  date_col: string;
  pet_col: string;
  status_col: string;
  code_col: string;
  concept_col: string;
  qty_col: string;
  unit_price_col: string;
  line_total_col: string;
  ticket_subtotal_label: string;
  ticket_tax_label: string;
  ticket_total_label: string;
  ticket_paid_label: string;
  ticket_pending_label: string;
  closing_title: string;
  no_tickets_label: string;
}

export function buildCustomerStatementPrintHtml(args: {
  view: StatementView;
  strings: CustomerStatementPrintStrings;
}): string {
  const { view, strings } = args;

  const ticket_blocks =
    view.tickets.length === 0
      ? `<div class="no-data">${escapeHtml(strings.no_tickets_label)}</div>`
      : view.tickets.map((ticket) => renderTicketBlock(ticket, strings)).join('');

  return `<!doctype html>
<html lang="es">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(strings.title)}</title>
    <style>
      :root {
        color-scheme: light;
        --text: #111111;
        --muted: #5f6368;
        --border: #d7dbe0;
        --border-strong: #9ca3af;
        --surface: #ffffff;
        --surface-alt: #f6f7f9;
      }

      @page {
        size: portrait;
        margin: 10mm;
      }

      html, body {
        margin: 0;
        padding: 0;
        background: var(--surface);
        color: var(--text);
        font-family: Arial, Helvetica, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .report {
        width: 100%;
        box-sizing: border-box;
      }

      .header {
        display: flex;
        align-items: end;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 14px;
        padding-bottom: 10px;
        border-bottom: 2px solid var(--text);
      }

      .title {
        margin: 0 0 4px;
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.02em;
      }

      .customer-name {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
      }

      .customer-meta {
        margin: 2px 0 0;
        font-size: 11px;
        color: var(--muted);
      }

      .generated-at {
        font-size: 11px;
        color: var(--muted);
        text-align: right;
        white-space: nowrap;
      }

      .generated-at strong {
        color: var(--text);
      }

      table {
        width: 100%;
        border-collapse: collapse;
      }

      .summary-table {
        margin-bottom: 16px;
      }

      .summary-table th,
      .summary-table td {
        border: 1px solid var(--border-strong);
        padding: 6px 8px;
        font-size: 11px;
        text-align: center;
      }

      .summary-table th {
        background: var(--text);
        color: #ffffff;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        font-weight: 700;
      }

      .amount {
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }

      .amount--balance {
        font-weight: 700;
      }

      .ticket-block {
        break-inside: avoid;
        page-break-inside: avoid;
        margin-bottom: 12px;
      }

      .ticket-table th,
      .ticket-table td,
      .lines-table th,
      .lines-table td {
        border: 1px solid var(--border);
        padding: 5px 6px;
        font-size: 10.5px;
        line-height: 1.25;
      }

      .ticket-table th {
        background: #dfe6ee;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        font-size: 10px;
        text-align: left;
      }

      .lines-table th {
        background: var(--surface-alt);
        text-transform: uppercase;
        letter-spacing: 0.03em;
        font-size: 10px;
        text-align: left;
      }

      .lines-table td.qty {
        text-align: center;
      }

      .ticket-summary-table td {
        border: 1px solid var(--border-strong);
        background: #eef2f6;
        padding: 5px 8px;
        font-size: 10.5px;
        font-weight: 700;
      }

      .closing {
        margin-top: 16px;
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .closing h2 {
        margin: 0 0 6px;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }

      .totals-table td {
        border: 1px solid var(--border-strong);
        padding: 6px 10px;
        font-size: 12px;
      }

      .grand-total-row td {
        background: #eef2f6;
        font-weight: 800;
        font-size: 13px;
      }

      .no-data {
        border: 1px solid var(--border);
        background: var(--surface-alt);
        padding: 18px;
        font-size: 13px;
        color: var(--muted);
      }
    </style>
  </head>
  <body>
    <main class="report">
      <header class="header">
        <div>
          <h1 class="title">${escapeHtml(strings.title)}</h1>
          <p class="customer-name">${escapeHtml(view.customer_name)}</p>
          <p class="customer-meta">${escapeHtml(strings.phone_label)}: ${escapeHtml(view.customer_phone)}${
            view.customer_email
              ? ` &middot; ${escapeHtml(strings.email_label)}: ${escapeHtml(view.customer_email)}`
              : ''
          }</p>
        </div>
        <div class="generated-at">
          ${escapeHtml(strings.generated_at_prefix)}:<br />
          <strong>${escapeHtml(view.generated_at_label)}</strong>
        </div>
      </header>

      <section>
        <table class="summary-table">
          <thead>
            <tr>
              <th>${escapeHtml(strings.tickets_label)}</th>
              <th>${escapeHtml(strings.liquidados_label)}</th>
              <th>${escapeHtml(strings.parciales_label)}</th>
              <th>${escapeHtml(strings.pendientes_label)}</th>
              <th>${escapeHtml(strings.sin_saldo_label)}</th>
              <th>${escapeHtml(strings.total_charges_label)}</th>
              <th>${escapeHtml(strings.paid_label)}</th>
              <th>${escapeHtml(strings.pending_balance_label)}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${view.summary.tickets_count}</td>
              <td>${view.summary.liquidados_count}</td>
              <td>${view.summary.parciales_count}</td>
              <td>${view.summary.pendientes_count}</td>
              <td>${view.summary.sin_saldo_count}</td>
              <td class="amount">${escapeHtml(view.summary.total_label)}</td>
              <td class="amount">${escapeHtml(view.summary.paid_label)}</td>
              <td class="amount amount--balance">${escapeHtml(view.summary.pending_label)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        ${ticket_blocks}
      </section>

      <section class="closing">
        <h2>${escapeHtml(strings.closing_title)}</h2>
        <table class="totals-table">
          <tbody>
            <tr>
              <td>${escapeHtml(strings.total_charges_label)}</td>
              <td class="amount">${escapeHtml(view.summary.total_label)}</td>
            </tr>
            <tr>
              <td>${escapeHtml(strings.paid_label)}</td>
              <td class="amount">${escapeHtml(view.summary.paid_label)}</td>
            </tr>
            <tr class="grand-total-row">
              <td>${escapeHtml(strings.pending_balance_label)}</td>
              <td class="amount">${escapeHtml(view.summary.pending_label)}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </main>
  </body>
</html>`;
}

function renderTicketBlock(
  ticket: StatementTicketView,
  strings: CustomerStatementPrintStrings,
): string {
  const line_rows = ticket.lines
    .map(
      (line) => `
    <tr>
      <td>${escapeHtml(line.item_code)}</td>
      <td>${escapeHtml(line.item_name)}</td>
      <td class="qty">${escapeHtml(line.quantity)}</td>
      <td class="amount">${escapeHtml(line.unit_price_label)}</td>
      <td class="amount">${escapeHtml(line.total_label)}</td>
    </tr>
  `,
    )
    .join('');

  return `
    <div class="ticket-block">
      <table class="ticket-table">
        <thead>
          <tr>
            <th>${escapeHtml(strings.ticket_col)}</th>
            <th>${escapeHtml(strings.date_col)}</th>
            <th>${escapeHtml(strings.pet_col)}</th>
            <th>${escapeHtml(strings.status_col)}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>${escapeHtml(ticket.ticket_label)}</strong></td>
            <td>${escapeHtml(ticket.date_label)}</td>
            <td>${escapeHtml(ticket.pet_name)}</td>
            <td>${escapeHtml(ticket.status_label)} &middot; ${escapeHtml(ticket.condition_label)}</td>
          </tr>
        </tbody>
      </table>
      <table class="lines-table">
        <thead>
          <tr>
            <th>${escapeHtml(strings.code_col)}</th>
            <th>${escapeHtml(strings.concept_col)}</th>
            <th>${escapeHtml(strings.qty_col)}</th>
            <th>${escapeHtml(strings.unit_price_col)}</th>
            <th>${escapeHtml(strings.line_total_col)}</th>
          </tr>
        </thead>
        <tbody>
          ${line_rows}
        </tbody>
      </table>
      <table class="ticket-summary-table">
        <tbody>
          <tr>
            <td>${escapeHtml(strings.ticket_subtotal_label)}: ${escapeHtml(ticket.subtotal_label)}</td>
            <td>${escapeHtml(strings.ticket_tax_label)}: ${escapeHtml(ticket.tax_label)}</td>
            <td>${escapeHtml(strings.ticket_total_label)}: ${escapeHtml(ticket.total_label)}</td>
            <td>${escapeHtml(strings.ticket_paid_label)}: ${escapeHtml(ticket.paid_label)}</td>
            <td>${escapeHtml(strings.ticket_pending_label)}: ${escapeHtml(ticket.pending_label)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
